import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const perceivedLevers = [
  {
    id: "staff",
    label: "Reduce staff workload & cost",
    pct: "~2–3%",
    color: "#3b82f6",
    hook: "Automate repetitive tasks — answering calls, verifying benefits, sending reminders — so the team needs fewer hours to keep up.",
    bullets: [
      {
        title: "AI phone receptionist",
        text: "Answers after-hours and overflow calls that today go to voicemail. Captures the 20–35% of scheduling calls currently unanswered — the fastest perceived win, but it only touches a thin slice of total margin.",
      },
      {
        title: "Scheduling bot",
        text: "Auto-books appointments, sends confirmations, handles reschedules and cancellations end-to-end. Pulls a few hours/week off the front desk — convenient, but doesn't meaningfully move payroll cost.",
      },
      {
        title: "Benefit verification tool",
        text: "Pings payers for real-time benefits before every visit so the back office isn't burning 30+ minutes per new patient on phone-tree verification. Cuts friction, but the actual staff hours recovered are marginal.",
      },
    ],
  },
  {
    id: "rcm",
    label: "Tighten the revenue cycle",
    pct: "~5%",
    color: "#6366f1",
    hook: "Plug the leaks — scrub claims before submission, work denials, recover underpayments, chase aging AR. Collect more of what the practice already produced.",
    bullets: [
      {
        title: "Claim scrubbing software",
        text: "Catches coding errors, missing attachments, and ID mismatches before the claim ever hits the payer. Denials drop 30–50% on first pass — but it only fixes errors at the moment of submission, not the systemic gaps upstream.",
      },
      {
        title: "Denial management AI",
        text: "Auto-reads EOBs, classifies rejections, and drafts appeals for the 67% of denials that normally never get resubmitted. Recovers real dollars, but leaves the root causes — coding, attachments, payer rules — unaddressed.",
      },
      {
        title: "AR automation",
        text: "Chases 30/60/90-day balances with automated statements and payment plan offers. Recovers aging AR that would otherwise be written off — but the leaks that created the aging AR in the first place keep happening.",
      },
    ],
  },
];

const PerceivedOpportunitySlide = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1200px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Title */}
        <div className="text-center mb-6 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight leading-[1.1]"
          >
            Most offices come in understanding there's a big problem:{" "}
            <span className="text-blue-600">10–15% of leaked margin they can capture.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45 }}
            className="text-[19px] lg:text-[22px] text-slate-700 font-medium max-w-[1000px] mx-auto leading-[1.5]"
          >
            They think that{" "}
            <span className="font-semibold text-slate-900">each AI tool they purchase</span>{" "}
            can chip away at a few percentage points.
          </motion.p>
        </div>

        {/* Bar visualization */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.1)] p-8"
        >
          {/* Bar */}
          <div className="relative">
            {/* Scale markers */}
            <div className="relative h-[18px] mb-1">
              {[0, 25, 38, 50, 75, 100].map((v) => (
                <div
                  key={v}
                  className="absolute -translate-x-1/2 top-0 text-[11px] font-semibold text-slate-500 tabular-nums"
                  style={{ left: `${v}%` }}
                >
                  ${v}K
                </div>
              ))}
            </div>

            {/* Bar container */}
            <div className="relative h-[72px] w-full rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
              {/* Take-home today: 0 → 38 */}
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "38%" }}
                transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-600 to-blue-500 flex items-center justify-center"
              >
                <div className="text-center text-white px-2">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] opacity-90">
                    Take-home today
                  </div>
                  <div className="text-[20px] font-extrabold leading-none tabular-nums">
                    $38K
                  </div>
                </div>
              </motion.div>

              {/* Goal delta: 38 → 50 */}
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "12%" }}
                transition={{ delay: 1.4, duration: 0.6, ease: "easeOut" }}
                className="absolute top-0 h-full bg-blue-200 flex items-center justify-center border-l-2 border-dashed border-blue-500"
                style={{ left: "38%" }}
              >
                <div className="text-center text-blue-900 px-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.12em]">
                    +$12K
                  </div>
                  <div className="text-[13px] font-extrabold leading-none tabular-nums">
                    AI lift
                  </div>
                </div>
              </motion.div>

              {/* Scale gridlines */}
              {[25, 50, 75].map((v) => (
                <div
                  key={v}
                  className="absolute top-0 h-full w-px bg-slate-300/60 pointer-events-none"
                  style={{ left: `${v}%` }}
                />
              ))}
            </div>

            {/* Goal marker below bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2, duration: 0.4 }}
              className="relative mt-2 h-[28px]"
            >
              <div
                className="absolute -translate-x-1/2 flex flex-col items-center"
                style={{ left: "50%" }}
              >
                <div className="w-px h-2 bg-blue-500" />
                <div className="text-[12px] font-bold text-blue-700 tabular-nums whitespace-nowrap">
                  Goal: $50K take-home
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* 2 levers — progressive disclosure (outside white card) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[12.5px] font-bold uppercase tracking-[0.15em] text-slate-500">
              They think they have 2 levers to pull with these AI tools:
            </div>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-600 hover:text-slate-900 transition-colors"
            >
              {expanded ? "Hide details" : "Show details"}
              <ChevronDown
                className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
            {perceivedLevers.map((lever, i) => (
              <motion.div
                key={lever.id}
                layout
                className="bg-white rounded-xl border border-slate-200 shadow-[0_12px_32px_-16px_rgba(15,23,42,0.1)] overflow-hidden border-t-[3px] flex flex-col"
                style={{ borderTopColor: lever.color }}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span
                      className="text-[10px] font-bold uppercase tracking-[0.18em] tabular-nums"
                      style={{ color: lever.color }}
                    >
                      0{i + 1}
                    </span>
                    <span
                      className="text-[12px] font-extrabold tabular-nums"
                      style={{ color: lever.color }}
                    >
                      {lever.pct}
                    </span>
                  </div>
                  <h3 className="text-[15px] font-bold text-slate-900 leading-tight">
                    {lever.label}
                  </h3>
                  <p className="text-[12px] text-slate-500 leading-snug mt-0.5">
                    {lever.hook}
                  </p>
                </div>
                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-3 flex flex-col gap-2.5 border-t border-slate-100">
                        {lever.bullets.map((b, j) => (
                          <div key={j}>
                            <h4
                              className="text-[12.5px] font-bold leading-tight mb-0.5"
                              style={{ color: lever.color }}
                            >
                              {b.title}
                            </h4>
                            <p className="text-[11.5px] text-slate-600 leading-[1.5]">
                              {b.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PerceivedOpportunitySlide;
